import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

/**
 * Non-blocking product-telemetry helper. Fire-and-forget inserts into
 * `public.product_events`. Never throws; failures are logged in dev only.
 *
 * Use for in-app feature usage — not social platform metrics (those live in
 * `analytics_events` with a CHECK-constrained `platform` column).
 *
 * Kill-signal example: `trackProductEvent('documents_row_clicked', { docId })`
 * then query `SELECT COUNT(DISTINCT user_id) FROM product_events
 *            WHERE event_name='documents_row_clicked'
 *              AND created_at > NOW() - INTERVAL '14 days'`.
 */
export async function trackProductEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
): Promise<void> {
  try {
    const { data: sessionRes } = await supabase.auth.getSession();
    const userId = sessionRes?.session?.user?.id;
    if (!userId) return;
    const { error } = await supabase.from('product_events').insert({
      user_id: userId,
      event_name: eventName,
      properties,
    });
    if (error && import.meta.env.DEV) {
      logger.warn('[trackProductEvent] insert failed:', eventName, error.message);
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      logger.warn('[trackProductEvent] unexpected error:', eventName, e);
    }
  }
}
